import React, { useState, useMemo, useContext } from 'react'
import { groupBy, orderBy, flatten, keyBy } from 'lodash'
import { useTeam } from 'app/frontend/base/config/context/team'
import { LootContext } from '../context'
import { difficulties } from 'app/frontend/types/custom/Difficulty'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import TeamWishlistEncounter from 'app/frontend/types/custom/TeamWishlistEncounter'
import { Button } from '@/components/ui/button'
import TeamWish from './TeamWish'
import { useLocation } from 'react-router-dom'

type Props = {
  teamWishlist: wowaudit.TeamWishlist
}

const TeamWishlistEncounters = ({ teamWishlist }: Props) => {
  const { team } = useTeam()
  const { hash: initialEncounterId } = useLocation()
  const { difficulty, valueDisplay } = useContext(LootContext)
  const difficultyIndex = difficulties.indexOf(difficulty)

  const charactersById = useMemo(
    () =>
      keyBy(
        team.members?.map((member) => member.characterReference) || [],
        'id',
      ),
    [],
  )

  const [activeEncounter, setActiveEncounter] = useState<
    TeamWishlistEncounter | 'all'
  >(
    (initialEncounterId
      ? initialEncounterId === '#all'
        ? 'all'
        : teamWishlist.encounters.find((e) => `#${e.id}` === initialEncounterId)
      : teamWishlist.encounters[0]) ?? teamWishlist.encounters[0],
  )

  const [showTrackUpgrades, setShowTrackUpgrades] = useState(
    !localStorage.getItem('hidingTrackUpgrades'),
  )

  const toggleTrackUpgrades = () => {
    if (showTrackUpgrades) {
      localStorage.setItem('hidingTrackUpgrades', 'true')
      setShowTrackUpgrades(false)
    } else {
      localStorage.removeItem('hidingTrackUpgrades')
      setShowTrackUpgrades(true)
    }
  }

  const baseWowheadUrl = `https://wowhead.com/`

  return (
    <div className='flex gap-2'>
      <div className='min-[625px]:w-56 min-[625px]:min-w-56 min-[625px]:max-w-56 mt-2'>
        <ul className='border-r-4'>
          {teamWishlist.encounters.map((encounter) => {
            const totalValue =
              (valueDisplay === 'percentage'
                ? encounter.total_percentage_value
                : encounter.total_absolute_value) ?? 0

            return (
              <li
                key={encounter.name}
                className={`flex gap-2 py-2 cursor-pointer hover:bg-accent ml-[-1rem] pl-[1.2rem] mr-[-4px] pr-3 border-r-4 ${
                  activeEncounter === encounter
                    ? 'bg-accent border-primary'
                    : 'border-transparent hover:border-border'
                }`}
                onClick={() => {
                  setActiveEncounter(encounter)
                  history.replaceState({}, '', `#${encounter.id}`)
                }}
              >
                <img
                  src={`https://data.wowaudit.com/img/${encounter.background}.jpg`}
                  className='w-10 h-10 min-w-10 min-h-10 rounded-full'
                />
                <div className='text-[15px] hidden min-[625px]:block'>
                  <div>{encounter.name}</div>
                  <div>
                    {totalValue > 0 ? (
                      <span className='text-success-foreground'>
                        {valueDisplay === 'percentage'
                          ? `${totalValue.toLocaleString('en-US', {
                              maximumFractionDigits: 2,
                              minimumFractionDigits: 2,
                            })}%`
                          : totalValue.toLocaleString('en-US', {
                              maximumFractionDigits: 0,
                            })}
                      </span>
                    ) : encounter.highest_manual_upgrade ? (
                      <span
                        className={`priority ${encounter.highest_manual_upgrade.toLowerCase()}`}
                      />
                    ) : (
                      <span className='text-muted-foreground'>nothing</span>
                    )}
                  </div>
                </div>
              </li>
            )
          })}
          <li
            className={`flex gap-2 py-2 cursor-pointer hover:bg-accent ml-[-1rem] pl-[1.2rem] pr-3 mr-[-4px] border-r-4 ${
              activeEncounter === 'all'
                ? 'bg-accent border-primary'
                : 'border-transparent hover:border-border'
            }`}
            onClick={() => {
              setActiveEncounter('all')
              history.replaceState({}, '', '#all')
            }}
          >
            All items
          </li>
        </ul>
      </div>
      <section className='grow py-3 pl-3'>
        <div className='flex justify-between items-center pb-2 gap-2 flex-wrap'>
          <h3 className='font-medium mb-0'>
            {activeEncounter === 'all' ? 'All items' : activeEncounter.name}
          </h3>
          <Button
            size='sm'
            variant='outline'
            className='w-40'
            onClick={toggleTrackUpgrades}
          >
            {showTrackUpgrades ? 'Hide' : 'Show'} track upgrades
          </Button>
        </div>

        {Object.values(
          groupBy(
            orderBy(
              activeEncounter === 'all'
                ? flatten(teamWishlist.encounters.map((e) => e.items))
                : activeEncounter.items,
              (i) => i.name,
            ),
            (i) => i.name,
          ),
        ).map((items) => {
          const wishlists = flatten(items.map((item) => item.wishlists))
          const charactersWithWish = wishlists.map((w) => w.character_id)
          const trackUpgrades = teamWishlist.metadata.filter(
            (character) =>
              showTrackUpgrades &&
              !charactersWithWish.includes(character.id) &&
              items[0].slot &&
              character.current_tracks[items[0].slot] &&
              (character.current_tracks[items[0].slot] as number) >
                difficultyIndex &&
              items.filter((item) =>
                item.eligible_classes.includes(character.class_id),
              ).length &&
              items.filter((item) =>
                item.eligible_specs.includes(character.default_spec_id ?? 0),
              ).length &&
              (items[0].slot !== 'trinket' ||
                character.current_tracks[items[0].slot] === 6),
          )

          return (
            <div key={items[0].id} className='mb-6 mt-2'>
              <div className='flex items-center gap-2 border-b pb-1.5'>
                {items.map((item, i) => (
                  <div className='flex items-center gap-1' key={item.id}>
                    {i > 0 ? <span>/</span> : null}
                    <a
                      target='_blank'
                      className={`inline-flex items-center gap-3 text-quality-${item.quality} text-[1rem]`}
                      rel='noreferrer'
                      href={`${baseWowheadUrl}item=${item.id}?ilvl=${
                        item.item_level
                      }${
                        item.bonus_ids.length
                          ? `&bonus=${item.bonus_ids.join(':')}`
                          : ''
                      }`}
                    >
                      {i === 0 ? (
                        <BlizzardIcon
                          size='medium'
                          iconName={item.icon}
                          className='border-none rounded-none'
                        />
                      ) : null}

                      {item.name}
                    </a>
                  </div>
                ))}
              </div>

              <div className='grid grid-cols-1 min-[750px]:grid-cols-2 min-[1000px]:grid-cols-3 min-[1300px]:grid-cols-4 gap-2 my-2'>
                {wishlists.length || trackUpgrades.length ? (
                  <React.Fragment>
                    {wishlists
                      .sort(
                        (a, b) =>
                          (b.score_by_spec.total[
                            valueDisplay === 'absolute' ? 'score' : 'percentage'
                          ] as number) -
                          (a.score_by_spec.total[
                            valueDisplay === 'absolute' ? 'score' : 'percentage'
                          ] as number),
                      )
                      .map((wishlist) => (
                        <TeamWish
                          key={wishlist.character_id}
                          characterInfo={charactersById[wishlist.character_id]}
                          wishlist={wishlist}
                          currentTrack={
                            showTrackUpgrades ? wishlist.equipped_track : null
                          }
                          equippedItemLevels={
                            teamWishlist.metadata.find(
                              (m) => m.id === wishlist.character_id,
                            )?.equipped_item_levels
                          }
                          item={items[0]}
                        />
                      ))}
                    {trackUpgrades.map((character) => (
                      <TeamWish
                        key={character.id}
                        characterInfo={charactersById[character.id]}
                        currentTrack={
                          items[0].slot
                            ? character.current_tracks[items[0].slot]
                            : null
                        }
                        equippedItemLevels={character.equipped_item_levels}
                        item={items[0]}
                      />
                    ))}
                  </React.Fragment>
                ) : (
                  <div className='italic text-muted-foreground'>
                    Not an upgrade
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </section>
    </div>
  )
}

export default TeamWishlistEncounters
